import React, { useState, useEffect } from 'react';
import SingleQuestionFlow from './SingleQuestionFlow';
import LiveResultsPanel from './LiveResultsPanel';
import CompletionPrompt from './CompletionPrompt';
import CombinedROIReport from './CombinedROIReport';
import ResultsSection from './ResultsSection';
import BrandingResultsSection from './branding/BrandingResultsSection';
import useSharedState from '../hooks/useSharedState';
import { websiteDefaults, brandingDefaults, sharedFields } from '../config/defaultValues';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMonitor, FiLayout } = FiIcons;

const calcWebsiteResults = (inputs) => {
  const { currentDonations, donationGrowthGoal, currentGrants, grantGrowthGoal, staffHourlyRate, currentWebsiteHours, eventAttendees, volunteerHours, websiteInvestment, organizationType, memberCount, membershipFee, memberGrowthGoal } = inputs;
  
  let totalAnnual = 0;
  const d1 = (currentDonations || 0) * (donationGrowthGoal || 0) / 100;
  const g1 = (currentGrants || 0) * (grantGrowthGoal || 0) / 100;
  const time = (currentWebsiteHours || 0) * (staffHourlyRate || 0) * 52;
  const evt = (eventAttendees || 0) * 0.1 * 50;
  const vol = (volunteerHours || 0) * 0.2 * (staffHourlyRate || 0);
  
  const membershipValue = organizationType === 'association' ? (memberCount || 0) * (memberGrowthGoal / 100) * (membershipFee || 0) : 0;
  
  totalAnnual = d1 + g1 + time + evt + vol + membershipValue;
  
  if (totalAnnual < (websiteInvestment || 0) * 0.5 && (d1 > 0 || membershipValue > 0)) {
    totalAnnual = (websiteInvestment || 0) * 0.5;
  }
  
  const roi = websiteInvestment > 0 ? (totalAnnual / websiteInvestment) * 100 : 0;
  const payback = totalAnnual > 0 ? Math.min((websiteInvestment / (totalAnnual / 12)), 24) : 0;
  const three = Math.max(totalAnnual * 3 - websiteInvestment, websiteInvestment * 0.5);

  return {
    donationIncrease: d1,
    grantIncrease: g1,
    annualTimeSavings: time,
    eventImprovement: evt,
    volunteerValue: vol,
    membershipValue,
    totalAnnualBenefits: totalAnnual,
    roi,
    paybackMonths: payback,
    threeYearValue: three,
    investment: websiteInvestment
  };
};

const calcBrandingResults = (inputs) => {
  const { annualOperatingBudget, socialMediaFollowers, emailListSize, monthlyWebsiteVisitors, donorRetentionGoal, brandingInvestment, brandConsistencyRating, organizationType, memberCount, membershipFee, memberGrowthGoal } = inputs;
  
  const cur = ((brandConsistencyRating || 5) / 10);
  const imp = Math.min(0.9, cur + 0.2);
  const donorBase = Math.max(emailListSize * 0.15, 50);
  const ret = (donorRetentionGoal / 100) * donorBase * 0.3 * 250;
  const pool = (socialMediaFollowers + (monthlyWebsiteVisitors || 0) * 12) * 0.01;
  const newD = pool * (imp - cur) * 250;
  
  const membershipValue = organizationType === 'association' ? (memberCount || 0) * (memberGrowthGoal / 100) * (membershipFee || 0) * 0.7 : 0;
  
  const annual = Math.max(ret + newD + (annualOperatingBudget || 0) * 0.01 + membershipValue, brandingInvestment * 0.5);
  const roi = brandingInvestment > 0 ? (annual / brandingInvestment) * 100 : 0;
  const payback = annual > 0 ? Math.min((brandingInvestment / (annual / 12)), 24) : 0;
  const three = Math.max(annual * 3 - brandingInvestment, brandingInvestment * 0.5);

  return {
    retentionImprovement: ret,
    newDonorValue: newD,
    membershipValue,
    totalAnnualBenefits: annual,
    roi,
    paybackMonths: payback,
    threeYearValue: three,
    investment: brandingInvestment,
    currentBrandStrength: cur,
    improvedBrandStrength: imp
  };
};

const TypeformCalculator = () => {
  const [mode, setMode] = useState('pick'); // pick, flow, prompt, final, combined
  const [calcType, setCalcType] = useState('website');
  const [completedTypes, setCompletedTypes] = useState([]);
  
  const [websiteInputs, setWebsiteInputs] = useSharedState('data', websiteDefaults, sharedFields);
  const [brandingInputs, setBrandingInputs] = useSharedState('brandData', brandingDefaults, sharedFields);
  
  const [websiteResults, setWebsiteResults] = useState({});
  const [brandingResults, setBrandingResults] = useState({});

  useEffect(() => {
    setWebsiteResults(calcWebsiteResults(websiteInputs));
  }, [websiteInputs]);

  useEffect(() => {
    setBrandingResults(calcBrandingResults(brandingInputs));
  }, [brandingInputs]);

  const handleComplete = () => {
    const newCompleted = [...new Set([...completedTypes, calcType])];
    setCompletedTypes(newCompleted);
    
    if (newCompleted.includes('website') && newCompleted.includes('branding')) {
      setMode('combined');
    } else {
      setMode('prompt');
    }
  };

  const handleRestart = () => {
    setCompletedTypes([]);
    setMode('pick');
  };

  // 1. Selection Screen
  if (mode === 'pick') {
    return (
      <div className="min-h-[80vh] flex items-center justify-center py-12">
        <div className="max-w-2xl w-full px-4 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">What would you like to calculate?</h1>
          <p className="text-gray-500 mb-10">Takes about 2 minutes. Tailored for Nonprofits & Associations.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <button onClick={() => { setCalcType('website'); setMode('flow'); }} className="bg-white border-2 border-gray-100 hover:border-blue-500 rounded-2xl p-8 text-left transition-all shadow-sm group">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 text-blue-600 group-hover:scale-110 transition-transform">
                <SafeIcon icon={FiMonitor} className="text-xl" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Website ROI</h3>
              <p className="text-gray-500 text-sm">Calculate return on donations, members, and grants.</p>
            </button>
            <button onClick={() => { setCalcType('branding'); setMode('flow'); }} className="bg-white border-2 border-gray-100 hover:border-purple-500 rounded-2xl p-8 text-left transition-all shadow-sm group">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4 text-purple-600 group-hover:scale-110 transition-transform">
                <SafeIcon icon={FiLayout} className="text-xl" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Branding ROI</h3>
              <p className="text-gray-500 text-sm">Measure trust, recognition, and member retention impact.</p>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 2. The "What's Next?" Screen
  if (mode === 'prompt') {
    return (
      <CompletionPrompt
        completedType={calcType}
        otherType={calcType === 'website' ? 'branding' : 'website'}
        currentResults={calcType === 'website' ? websiteResults : brandingResults}
        onStartOther={() => {
          setCalcType(calcType === 'website' ? 'branding' : 'website');
          setMode('flow');
        }}
        onSkip={() => setMode('final')}
        onRestart={handleRestart}
      />
    );
  }

  // 3. Combined View (Both Finished)
  if (mode === 'combined') {
    return <CombinedROIReport websiteResults={websiteResults} brandingResults={brandingResults} onRestart={handleRestart} />;
  }

  // 4. Final View for Single Calculator (Lead Capture)
  if (mode === 'final') {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Your {calcType === 'website' ? 'Website' : 'Branding'} Analysis is Ready</h2>
          <p className="text-gray-600">Unlock your full report below to see the complete breakdown and strategic roadmap.</p>
        </div>
        {calcType === 'website' ? (
          <ResultsSection results={websiteResults} inputs={websiteInputs} />
        ) : (
          <BrandingResultsSection results={brandingResults} inputs={brandingInputs} />
        )}
        <div className="mt-8 text-center">
          <button onClick={handleRestart} className="text-gray-400 hover:text-gray-600 text-sm flex items-center gap-1 mx-auto">
            <SafeIcon icon={FiIcons.FiRefreshCw} /> Start over
          </button>
        </div>
      </div>
    );
  }

  // 5. Active Flow Mode
  const activeInputs = calcType === 'website' ? websiteInputs : brandingInputs;
  const activeSetter = calcType === 'website' ? setWebsiteInputs : setBrandingInputs;
  const activeResults = calcType === 'website' ? websiteResults : brandingResults;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="grid lg:grid-cols-2 gap-8 items-start">
        <SingleQuestionFlow 
          calcType={calcType} 
          inputs={activeInputs} 
          updateInput={activeSetter} 
          onRestart={handleRestart} 
          onComplete={handleComplete} 
        />
        <div className="lg:sticky lg:top-6">
          <LiveResultsPanel calcType={calcType} results={activeResults} inputs={activeInputs} />
        </div>
      </div>
    </div>
  );
};

export default TypeformCalculator;